import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import React, { useState } from "react";
import {
  FiActivity,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiGlobe,
  FiMapPin,
  FiNavigation,
  FiTrendingUp,
  FiWifi
} from "react-icons/fi";

interface TimelineItem {
  step: number;
  location: string;
  ip: string;
  requests: number;
  timestamp: string;
  duration: string;
  details?: {
    device?: string;
    browser?: string;
    referrer?: string;
    pagesViewed?: number;
    engagementScore?: number;
  };
}

interface LocationTimelineProps {
  items: TimelineItem[];
}

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return {
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    }),
    date: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }),
    relative: getRelativeTime(date)
  };
};

const getRelativeTime = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  return "Just now";
};

const LocationTimeline: React.FC<LocationTimelineProps> = ({ items }) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  const toggleExpanded = (step: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(step)) {
      newExpanded.delete(step);
    } else {
      newExpanded.add(step);
    }
    setExpandedItems(newExpanded);
  };

  const totalRequests = items.reduce((sum, item) => sum + item.requests, 0);
  const totalDuration = items.reduce((sum, item) => {
    const minutes = parseInt(item.duration) || 0;
    return sum + minutes;
  }, 0);

  const getStepColor = (step: number, total: number) => {
    if (step === 1) return "green";
    if (step === total) return "red";
    return "blue";
  };

  const getStepIcon = (step: number, total: number) => {
    if (step === 1) return FiNavigation;
    if (step === total) return FiMapPin;
    return FiActivity;
  };

  return (
    <Box>
      {/* Header */}
      <Box
        p={5}
        bg="gradient.to-r"
        bgGradient="linear(to-r, blue.50, purple.50)"
        rounded="xl"
        mb={6}
      >
        <HStack justify="space-between" mb={4}>
          <HStack gap={3}>
            <Box
              p={3}
              bg="white"
              rounded="lg"
              shadow="md"
            >
              <Icon as={FiCalendar} boxSize={6} color="blue.500" />
            </Box>
            <VStack align="start" gap={0}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Location Timeline
              </Text>
              <Text fontSize="sm" color="gray.600">
                Journey through {items.length} locations in Toronto
              </Text>
            </VStack>
          </HStack>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
          >
            <HStack gap={1}>
              <Icon as={FiChevronDown} />
              <Text>Export Timeline</Text>
            </HStack>
          </Button>
        </HStack>

        {/* Summary Stats */}
        <SimpleGrid columns={4} gap={4}>
          <Box p={3} bg="white" rounded="lg" shadow="sm">
            <HStack gap={2}>
              <Icon as={FiMapPin} color="blue.500" />
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="gray.500">Locations</Text>
                <Text fontSize="lg" fontWeight="bold">{items.length}</Text>
              </VStack>
            </HStack>
          </Box>
          <Box p={3} bg="white" rounded="lg" shadow="sm">
            <HStack gap={2}>
              <Icon as={FiActivity} color="purple.500" />
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="gray.500">Total Requests</Text>
                <Text fontSize="lg" fontWeight="bold">{totalRequests}</Text>
              </VStack>
            </HStack>
          </Box>
          <Box p={3} bg="white" rounded="lg" shadow="sm">
            <HStack gap={2}>
              <Icon as={FiClock} color="green.500" />
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="gray.500">Total Duration</Text>
                <Text fontSize="lg" fontWeight="bold">{totalDuration} min</Text>
              </VStack>
            </HStack>
          </Box>
          <Box p={3} bg="white" rounded="lg" shadow="sm">
            <HStack gap={2}>
              <Icon as={FiTrendingUp} color="orange.500" />
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="gray.500">Avg. Engagement</Text>
                <Text fontSize="lg" fontWeight="bold">85%</Text>
              </VStack>
            </HStack>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Timeline Items */}
      <VStack align="stretch" gap={0} position="relative">
        {/* Vertical Line */}
        <Box
          position="absolute"
          left="32px"
          top="40px"
          bottom="40px"
          w="2px"
          bg="gray.200"
          zIndex={0}
        />

        {items.map((item) => {
          const isExpanded = expandedItems.has(item.step);
          const isHovered = hoveredItem === item.step;
          const stepColor = getStepColor(item.step, items.length);
          const StepIcon = getStepIcon(item.step, items.length);
          const formatted = formatTimestamp(item.timestamp);

          return (
            <Box
              key={item.step}
              position="relative"
              onMouseEnter={() => setHoveredItem(item.step)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <HStack gap={4} align="start">
                {/* Step Circle */}
                <Box position="relative" zIndex={2}>
                  <Box
                    w="64px"
                    h="64px"
                    bg="white"
                    rounded="full"
                    shadow={isHovered ? "xl" : "md"}
                    border="3px"
                    borderColor={`${stepColor}.400`}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s"
                    transform={isHovered ? "scale(1.1)" : "scale(1)"}
                  >
                    <VStack gap={0}>
                      <Icon
                        as={StepIcon}
                        boxSize={5}
                        color={`${stepColor}.500`}
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color={`${stepColor}.600`}
                      >
                        {item.step}
                      </Text>
                    </VStack>
                  </Box>
                  {item.step === 1 && (
                    <Badge
                      position="absolute"
                      top="-2"
                      right="-2"
                      colorScheme="green"
                      fontSize="2xs"
                      rounded="full"
                      px={2}
                    >
                      START
                    </Badge>
                  )}
                  {item.step === items.length && (
                    <Badge
                      position="absolute"
                      top="-2"
                      right="-2"
                      colorScheme="red"
                      fontSize="2xs"
                      rounded="full"
                      px={2}
                    >
                      END
                    </Badge>
                  )}
                </Box>

                {/* Content Card */}
                <Box
                  flex={1}
                  mb={6}
                  p={5}
                  bg="white"
                  rounded="xl"
                  border="1px"
                  borderColor={isHovered ? `${stepColor}.200` : "gray.200"}
                  shadow={isHovered ? "lg" : "md"}
                  transition="all 0.3s"
                  transform={isHovered ? "translateX(4px)" : "translateX(0)"}
                >
                  <HStack justify="space-between" mb={3}>
                    <VStack align="start" gap={1}>
                      <HStack gap={2}>
                        <Icon as={FiMapPin} color={`${stepColor}.500`} />
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                          {item.location}
                        </Text>
                      </HStack>
                      <HStack gap={3} flexWrap="wrap">
                        <Badge colorScheme="gray" variant="subtle">
                          <HStack gap={1}>
                            <Icon as={FiWifi} boxSize={3} />
                            <Text>{item.ip}</Text>
                          </HStack>
                        </Badge>
                        <Badge colorScheme="blue" variant="subtle">
                          <HStack gap={1}>
                            <Icon as={FiActivity} boxSize={3} />
                            <Text>{item.requests} requests</Text>
                          </HStack>
                        </Badge>
                        <Badge colorScheme="purple" variant="subtle">
                          <HStack gap={1}>
                            <Icon as={FiClock} boxSize={3} />
                            <Text>{item.duration}</Text>
                          </HStack>
                        </Badge>
                      </HStack>
                    </VStack>
                    <VStack align="end" gap={1}>
                      <Badge 
                        colorScheme={stepColor} 
                        fontSize="xs"
                        title={formatted.date + " " + formatted.time}
                      >
                        {formatted.relative}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {formatted.time}
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Progress Bar */}
                  <Box mb={3}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.500">
                        Activity Level
                      </Text>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {Math.round((item.requests / totalRequests) * 100)}%
                      </Text>
                    </HStack>
                    <Box
                      position="relative"
                      height="6px"
                      bg="gray.200"
                      rounded="full"
                      overflow="hidden"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        height="100%"
                        width={`${(item.requests / totalRequests) * 100}%`}
                        bg={`${stepColor}.500`}
                        rounded="full"
                        transition="width 0.3s ease"
                      />
                    </Box>
                  </Box>

                  {/* Expandable Details */}
                  {item.details && (
                    <>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleExpanded(item.step)}
                        color="gray.600"
                        _hover={{ color: `${stepColor}.500` }}
                      >
                        <HStack gap={1}>
                          <Text>{isExpanded ? "Hide" : "Show"} Details</Text>
                          <Icon
                            as={isExpanded ? FiChevronUp : FiChevronDown}
                            boxSize={3}
                          />
                        </HStack>
                      </Button>
                      {isExpanded && (
                        <Box
                          mt={3}
                          pt={3}
                          borderTop="1px"
                          borderColor="gray.100"
                        >
                          <SimpleGrid columns={2} gap={3}>
                            {item.details.device && (
                              <HStack gap={2}>
                                <Icon as={FiGlobe} color="gray.400" boxSize={3} />
                                <VStack align="start" gap={0}>
                                  <Text fontSize="xs" color="gray.500">Device</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.details.device}
                                  </Text>
                                </VStack>
                              </HStack>
                            )}
                            {item.details.browser && (
                              <HStack gap={2}>
                                <Icon as={FiGlobe} color="gray.400" boxSize={3} />
                                <VStack align="start" gap={0}>
                                  <Text fontSize="xs" color="gray.500">Browser</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.details.browser}
                                  </Text>
                                </VStack>
                              </HStack>
                            )}
                            {item.details.pagesViewed && (
                              <HStack gap={2}>
                                <Icon as={FiActivity} color="gray.400" boxSize={3} />
                                <VStack align="start" gap={0}>
                                  <Text fontSize="xs" color="gray.500">Pages Viewed</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.details.pagesViewed}
                                  </Text>
                                </VStack>
                              </HStack>
                            )}
                            {item.details.engagementScore && (
                              <HStack gap={2}>
                                <Icon as={FiTrendingUp} color="gray.400" boxSize={3} />
                                <VStack align="start" gap={0}>
                                  <Text fontSize="xs" color="gray.500">Engagement</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.details.engagementScore}%
                                  </Text>
                                </VStack>
                              </HStack>
                            )}
                          </SimpleGrid>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default LocationTimeline;